<template>
  <v-flex
    xs12
  >
    <v-expansion-panel>
      <v-expansion-panel-content>
        <template v-slot:header>
          <div>
            <div>
              <h3 class="title font-weight-regular pt-3">
                {{ $t('StarPrinter') }}
              </h3>
            </div>
            <div class="pt-2">
              <div>{{ $t('Configure StarPrinter integration for automated documents printing.') }}</div>
            </div>
          </div>
        </template>

        <v-divider/>

        <v-layout
          align-center
          justify-center
          row
        >
          <v-flex
            xs12
            class="pa-4"
          >
            <v-form>
              <v-layout row>
                <v-flex
                  class="px-4"
                  xs6
                  lg2>
                  <span class="title d-block">{{ $t('Printers') }}</span>
                  <span class="caption d-block">{{ $t('List of available printers') }}</span>
                </v-flex>
                <v-flex
                  xs6
                  lg6>
                  <v-switch
                    v-model="config.general.star_printer.enabled"
                    :label="$t('Enabled')"
                    @change="save()"
                  />
                  <v-layout
                    v-if="config.general.star_printer"
                    row
                    wrap
                  >
                    <v-flex md10>
                      <v-chip
                        v-for="printer in config.general.star_printer.printers || []"
                        :key="printer.mac"
                      >
                        {{ printer.name }} | {{ printer.mac }}
                        <v-icon
                          right
                          @click="removePrinter(printer)"
                        >
                          close
                        </v-icon>
                      </v-chip>
                    </v-flex>
                    <v-flex md2>
                      <v-btn
                        :disabled="addingPrinter"
                        block
                        color="primary"
                        @click="addingPrinter = true"
                      >{{ $t('Add') }}</v-btn>
                    </v-flex>
                    <v-flex
                      v-if="addingPrinter"
                      md12>
                      <v-layout row>
                        <v-flex md5>
                          <v-text-field
                            v-model="printer.name"
                            :label="$t('Name')"
                          />
                        </v-flex>
                        <v-flex md5>
                          <v-text-field
                            v-model="printer.mac"
                            :label="$t('MAC Address')"
                            :rules="macRules"
                          />
                        </v-flex>
                        <v-flex md1>
                          <v-btn
                            icon
                            @click="closeAddingPrinter()"
                          >
                            <v-icon>close</v-icon>
                          </v-btn>
                        </v-flex>
                        <v-flex md1>
                          <v-btn
                            icon
                            @click="savePrinter()">
                            <v-icon>check</v-icon>
                          </v-btn>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-divider class="my-4"/>

              <v-layout row>
                <v-flex
                  class="px-4"
                  xs6
                  lg2>
                  <span class="title d-block">{{ $t('Documents') }}</span>
                  <span class="caption d-block">{{ $t('Documents (invoice/receipt) printing settings') }}</span>
                </v-flex>
                <v-flex
                  lg6
                  xs6>
                  <v-combobox
                    v-model="docPrinters"
                    :label="$t('List of allowed printers')"
                    :items="config.general.star_printer.printers || []"
                    :return-object="true"
                    item-text="name"
                    item-value="mac"
                    multiple
                    chips
                    deletable-chips
                  />
                  <v-switch
                    v-model="config.general.star_printer.automatic_document"
                    :label="$t('Automatic printing')"
                    @change="save()"
                  />
                  <v-select
                    v-model="config.general.star_printer.document_events"
                    :items="events"
                    :label="$t('Printing events')"
                    :disabled="!config.general.star_printer.automatic_document"
                    multiple
                    @change="save"
                  />

                  <v-text-field
                    v-model="config.general.star_printer.footer"
                    :label="$t('Footer printed with every document')"
                    @change="save()"
                  />
                  <v-switch
                    v-model="config.general.star_printer.document_buzzer"
                    :label="$t('Buzz on print')"
                    @change="save()"
                  />
                  <v-switch
                    v-model="config.general.star_printer.document_drawer"
                    :label="$t('Open cash drawer on print')"
                    @change="save()"
                  />
                </v-flex>
              </v-layout>

              <v-divider class="my-4"/>

              <v-layout row>
                <v-flex
                  class="px-4"
                  xs6
                  lg2>
                  <span class="title d-block">{{ $t('Slips') }}</span>
                  <span class="caption d-block">{{ $t('Slips printing settings') }}</span>
                </v-flex>
                <v-flex
                  lg6
                  xs6>
                  <v-combobox
                    v-model="slipPrinters"
                    :label="$t('List of allowed printers')"
                    :items="config.general.star_printer.printers || []"
                    :return-object="true"
                    item-text="name"
                    item-value="mac"
                    multiple
                    chips
                    deletable-chips
                  />
                  <v-switch
                    v-model="config.general.star_printer.automatic_slip"
                    :label="$t('Automatic printing')"
                    @change="save()"
                  />
                  <v-select
                    v-model="config.general.star_printer.slip_events"
                    :items="events"
                    :label="$t('Printing events')"
                    :disabled="!config.general.star_printer.automatic_slip"
                    multiple
                    @change="save"
                  />
                  <v-switch
                    v-model="config.general.star_printer.slip_buzzer"
                    :label="$t('Buzz on print')"
                    @change="save()"
                  />
                  <v-switch
                    v-model="config.general.star_printer.slip_drawer"
                    :label="$t('Open cash drawer on print')"
                    @change="save()"
                  />
                </v-flex>
              </v-layout>

            </v-form>
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-flex>
</template>

<script>
export default {
  name: 'StarPrinterSettings',

  props: {
    config: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      loading: false,
      addingPrinter: false,
      printer: {
        name: '',
        mac: ''
      },
      events: ['invoice', 'receipt'],
      macRules: [
        (mac) => {
          const regex = /^(([A-Fa-f0-9]{2}[:]){5}[A-Fa-f0-9]{2}[,]?)+$/i
          if (regex.test(mac)) {
            return true
          }

          return this.$t('Invalid MAC address')
        }
      ]
    }
  },

  computed: {
    slipPrinters: {
      get () {
        const printers = this.config.general.star_printer.slip || []
        if (!printers || !printers.length) {
          return []
        }

        return this.config.general.star_printer.printers.filter(printer => printers.includes(printer.mac))
      },
      set (value) {
        if (value && value.length) {
          value = value.map(printer => printer.mac)
        }
        this.config.general.star_printer.slip = value
        this.save()
      }
    },
    docPrinters: {
      get () {
        const printers = this.config.general.star_printer.document || []
        if (!printers || !printers.length) {
          return []
        }

        return this.config.general.star_printer.printers.filter(printer => printers.includes(printer.mac))
      },
      set (value) {
        if (value && value.length) {
          value = value.map(printer => printer.mac)
        }
        this.config.general.star_printer.document = value
        this.save()
      }
    }
  },

  methods: {
    savePrinter () {
      if (!this.printer.name || !this.printer.mac) {
        return
      }

      if (!this.config.general.star_printer.printers) {
        this.config.general.star_printer.printers = []
      }

      const exists = this.config.general.star_printer.printers.find(e => e.mac === this.printer.mac)
      if (exists) {
        this.closeAddingPrinter()
        return
      }

      this.config.general.star_printer.printers.push(this.printer)
      this.closeAddingPrinter()
      this.save()
    },

    closeAddingPrinter () {
      this.addingPrinter = false
      this.printer = {
        name: '',
        mac: ''
      }
    },

    removePrinter (printer) {
      this.config.general.star_printer.printers = this.config.general.star_printer.printers.filter(el => el.mac !== printer.mac)
      if (this.config.general.star_printer.document.length > 0) {
        this.config.general.star_printer.document = this.config.general.star_printer.document.filter(el => el !== printer.mac)
      }

      if (this.config.general.star_printer.slip.length > 0) {
        this.config.general.star_printer.slip = this.config.general.star_printer.slip.filter(el => el !== printer.mac)
      }

      this.save()
    },

    async save () {
      if (this.config.length === 0) {
        return
      }

      const settings = this.$http.make('settings', {
        data: this.config.general.star_printer
      })

      settings.code_url = 'general/star_printer'
      this.loading = true
      try {
        await settings.save()
        this.$toast('Settings saved')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
